import { Question } from '@jupiterone/jupiter-types';
import { useLazyQuery } from '@apollo/client';
import { suggest } from '@jupiterone/query-language-autocomplete';
import {
  isAQuery,
  ListQuestionsQuery,
  useSettings,
} from '@jupiterone/jupiter-web-toolkit';
import * as MockHook from '../../util/useMockHook';
import { useState, useEffect, useMemo } from 'react';
import { useBooleanState } from 'react-use-object-state';
import { useDebouncedCallback } from 'use-debounce';
import { J1QL_AUTOCOMPLETE_PATH } from 'jupiter-web-landing/src/constants';
import { getFestivePromotion } from 'jupiter-web-landing/src/components/FestivePromotions/FestivePromotion';

export interface J1SearchbarProps {
  libraryOpen?: boolean;
  hasResults: boolean;
  lastEditableQuery: string;
  showGradientBackground: boolean;
  onAddQuestion: (question: Question) => void;
  onClearAllResults: VoidFunction;
  onSearch: (searchText: string, includeDeleted?: boolean) => void;
  toggleQuestionList: VoidFunction;
  toggleJ1QLHelp?: VoidFunction;
  onOpenVisualQueryBuilder?: VoidFunction;
}

export const MOCK_SUGGESTED_QUESTIONS = [
  {
    name: 'test question 1',
    accountId: 'j1dev',
    description: 'Returns a list of lambda functions by runtime.',
    id: 'ae873d64-8c5f-481a-b40f-55a7567edc12',
    integrationDefinitionId: '7a669809-6e55-45b9-bf23-aa27613118e9',
    queries: [
      {
        name: 'nodejs6.10 (EOL)',
        query:
          "Find aws_lambda_function with runtime = 'nodejs6.10' as f\norder by f.tag.AccountName\n",
      },
    ],
    sourceId: 'managed:aws',
    tags: ['aws', 'lambda', 'serverless'],
    title: 'List Lambda Functions by Runtime',
    lastUpdatedTimestamp: 0,
  },
  {
    name: 'test question 2',
    accountId: 'j1dev',
    compliance: [
      {
        controls: ['cp-threat-firewall', 'cp-threat-webapp'],
        standard: 'default',
      },
    ],
    description:
      'Lists Firewall instances and the Networks they each protects.',
    id: '58d5f4bd-a661-47f3-99fc-78f47bc0efab',
    queries: [
      {
        query:
          'Find Firewall as f \n  that PROTECTS Network as n \n…playName as firewall,\n  n.displayName as network\n',
      },
    ],
    sourceId: 'managed:infra',
    tags: ['infra', 'network', 'compliance'],
    title:
      'Show listing of network layer firewall protection across all my environments.',
    lastUpdatedTimestamp: 0,
  },
];

const useJ1SearchbarLogic = ({
  libraryOpen,
  hasResults,
  lastEditableQuery,
  showGradientBackground,
  onAddQuestion,
  onClearAllResults,
  onSearch,
  toggleQuestionList: handleToggleQuestionList,
  toggleJ1QLHelp: handleToggleJ1QLHelp,
  onOpenVisualQueryBuilder: handleOpenVisualQueryBuilder,
}: J1SearchbarProps) => {
  const [searchText, setSearchText] = useState<
    J1SearchbarProps['lastEditableQuery']
  >(lastEditableQuery);
  const [includeDeleted, setIncludeDeleted] = useState<boolean>(false);
  const [showSuggestedQuestions, setShowSuggestedQuestions] = useState<boolean>(
    false
  );
  const [
    querySuggestedQuestions,
    { data, loading: loadingQuestions },
  ] = useLazyQuery(ListQuestionsQuery, {
    fetchPolicy: `network-only`,
  });

  const [errorMessage, setErrorMessage] = useState<string>('');
  const handleClearErrorMessage = () => setErrorMessage('');

  const showOptionsDropdown = useBooleanState(true);
  const [autocompleteSetting, saveAutocompleteSetting] = useSettings<
    boolean | undefined
  >('user', J1QL_AUTOCOMPLETE_PATH);
  const autocompleteEnabled = autocompleteSetting?.data !== false ?? true;

  useEffect(() => {
    if (searchText !== lastEditableQuery) setSearchText(lastEditableQuery);
  }, [lastEditableQuery]);

  const suggestedQuestions: Question[] = useMemo(() => {
    if (showSuggestedQuestions && data) {
      return data.questions.questions;
    }
    return [];
  }, [data, showSuggestedQuestions]);

  const validateSearchTextAsQuery = (searchText: string) => {
    try {
      if (searchText && !getFestivePromotion(searchText)) {
        suggest(searchText);
      }
      setErrorMessage('');
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  const fetchSuggestedQuestions = async (searchText: string) => {
    querySuggestedQuestions({
      variables: {
        searchQuery: searchText,
        limit: 5,
      },
    });
    setShowSuggestedQuestions(true);
  };

  const [parseSearchText] = useDebouncedCallback(async (searchText: string) => {
    const trimmedSearchText = searchText.trim();
    if (!trimmedSearchText) return;
    if (isAQuery(trimmedSearchText)) {
      validateSearchTextAsQuery(trimmedSearchText);
    } else {
      await fetchSuggestedQuestions(trimmedSearchText);
    }
  }, 250);

  const handleChangeSearchText = async (searchText: string) => {
    setSearchText(searchText);
    handleClearErrorMessage();
    setShowSuggestedQuestions(false);
    showOptionsDropdown.setTrue();
    await parseSearchText(searchText);
  };

  const handleSelectQuestion = (question: Question) => {
    handleClearErrorMessage();
    setShowSuggestedQuestions(false);
    showOptionsDropdown.setFalse();
    onAddQuestion(question);
  };

  const handleSearch = () => {
    setShowSuggestedQuestions(false);
    showOptionsDropdown.setFalse();
    onSearch(searchText, includeDeleted);
  };

  const handleClearSearches = () => {
    setSearchText('');
    setShowSuggestedQuestions(false);
    handleClearErrorMessage();
    onClearAllResults();
  };
  return {
    errorMessage,
    searchText,
    suggestedQuestions,
    hasResults,
    includeDeleted,
    libraryOpen,
    enabledDropdownOptions: showOptionsDropdown.state,
    autocompleteEnabled,
    showGradientBackground,
    handleHideDropdown: () => {
      showOptionsDropdown.setFalse();
    },
    showSuggestedQuestions: !loadingQuestions && showSuggestedQuestions,
    handleShowDropdownOptions: showOptionsDropdown.setTrue,
    handleToggleQuestionList,
    handleChangeSearchText,
    handleSearch,
    handleHideSuggestedQuestions: () => setShowSuggestedQuestions(false),
    handleSelectQuestion,
    handleIncludeDeletedEntities: setIncludeDeleted,
    handleEnableAutocomplete: saveAutocompleteSetting,
    handleOpenVisualQueryBuilder,
    handleToggleJ1QLHelp,
    handleClearSearches,
  };
};

export type J1SearchbarComponentProps = ReturnType<typeof useJ1SearchbarLogic>;
export const MOCK_J1_SEARCH_BAR_PROPS: J1SearchbarComponentProps = {
  errorMessage: '',
  searchText: '',
  suggestedQuestions: [],
  hasResults: false,
  includeDeleted: false,
  libraryOpen: false,
  enabledDropdownOptions: false,
  autocompleteEnabled: false,
  showSuggestedQuestions: false,
  handleHideDropdown: MockHook.mockFunction,
  handleShowDropdownOptions: MockHook.mockFunction,
  handleToggleQuestionList: MockHook.mockFunction,
  handleChangeSearchText: MockHook.mockFunction,
  handleSearch: MockHook.mockFunction,
  handleHideSuggestedQuestions: MockHook.mockFunction,
  handleSelectQuestion: MockHook.mockFunction,
  handleIncludeDeletedEntities: MockHook.mockFunction,
  handleEnableAutocomplete: MockHook.mockFunction,
  handleOpenVisualQueryBuilder: MockHook.mockFunction,
  handleToggleJ1QLHelp: MockHook.mockFunction,
  handleClearSearches: MockHook.mockFunction,
  showGradientBackground: false,
};
export default MockHook.useMockHook(
  useJ1SearchbarLogic,
  MOCK_J1_SEARCH_BAR_PROPS
);
